// PARTNER MANAGEMENT - the company's partner organisations and their people
// (owner, 2026-09-21: "add a page under coordination ... for partner
// management only for super admin and company admin").
//
// THE CORE, AND WHERE EACH PERSON WORKS (owner, 2026-10-15: "the partner
// core will combine users from all projects but only show specific people on
// specific projects"). Each partner's people are listed combined across the
// company's projects, with the projects each one is on. Renaming or
// re-titling a person applies wherever they are; adding asks which projects;
// nobody spreads to another project by themselves. Nothing is emailed -
// inviting stays on each project's Participants page, because an invitation
// names a project. Rates stay per project and are not shown or touched here.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Empty, Input, Modal, Popconfirm, Segmented, Select, Skeleton, Switch, Table, Tag, Tooltip, message } from "antd";
import { CopyOutlined, PlusOutlined, ReloadOutlined, TeamOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { Link, Redirect } from "react-router-dom";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import { T } from "pages/Issue/theme";
import { addPartnerPerson, getPartnerDirectory, grantPartnerAccount, removePartnerPerson, setPartnerPersonProjects, updatePartnerPerson } from "api/partners/partnerDirectoryApi";
import { copyParticipants, removeParticipant } from "api/participants/participantsApi";
import PartnerAccessDialog, { AccountCell } from "./PartnerAccessDialog";
import PartnerPersonModal, { PERSON_KINDS } from "components/Partners/PartnerPersonModal";
import CoordinationAccessGrid from "components/Partners/CoordinationAccessGrid";
import { PartnerWithTrades } from "components/Partners/PartnerTradeLabel";
import { getDiscipline } from "api/discipline/getDiscipline";
import { matchOption } from "components/SearchSelect/searchSelect";

const KINDS = PERSON_KINDS;
// WHICH OF THE THREE a row is, for the edit control. The directory returns
// person/supervisor/executive as INDEPENDENT booleans because somebody can
// legitimately be both - see `both` below, which is why this is only ever a
// starting value and never used to write.
const kindOf = (p) => (p.executive ? "executive" : p.supervisor ? "supervisor" : "person");
const kindTag = (p) => {
  if (p.executive) return <Tag color="purple">Executive</Tag>;
  if (p.supervisor) return <Tag color="blue">Supervisor</Tag>;
  return null;
};

// Every trade a partner holds on any of the account's projects, as names.
function orgTrades(org, disciplines) {
  const out = [];
  for (const p of (org && org.projects) || []) {
    for (const t of p.trades || []) {
      const d = (disciplines || []).find((x) => String(x.key).toLowerCase() === String(t).toLowerCase());
      const name = d ? (d.value || d.key) : String(t);
      if (!out.includes(name)) out.push(name);
    }
  }
  return out;
}

// `embedded` (owner, 2026-10-16): rendered inside the one Partners view
// (./PartnersHub), which carries the breadcrumb and the title.
export default function PartnerManagementPage({ embedded = false } = {}) {
  const userInfo = useSelector((s) => s.user?.userInfo);
  const allowed = !!(userInfo?.admin || userInfo?.companyAdmin);
  // Trade NAMES for each partner's trades (owner, 2026-10-16: the trade
  // under every partner) - the account's discipline list maps the keys.
  const [disciplines, setDisciplines] = useState([]);
  useEffect(() => {
    if (!userInfo?.company) return;
    getDiscipline(userInfo.company).then((l) => setDisciplines(Array.isArray(l) ? l : [])).catch(() => setDisciplines([]));
  }, [userInfo?.company]);
  const [state, setState] = useState({ loading: true, orgs: [], projects: [], error: "" });
  const [picked, setPicked] = useState("");
  const [filter, setFilter] = useState("");
  const [adding, setAdding] = useState(null);      // form for a new person
  const [editing, setEditing] = useState(null);    // form for an existing one
  const [projectsFor, setProjectsFor] = useState(null); // { person, picked: [projectId] }
  // { email, name } of the person whose access is open (owner, 2026-10-05).
  // One request per person looked at, rather than a join on every row.
  const [access, setAccess] = useState(null);
  const [busy, setBusy] = useState(false);
  // ADDING A PARTNER TO ANOTHER PROJECT, and CLONING partners between
  // projects (owner, 2026-09-21: "there is no way to add partner users to
  // another project on the partners tab, add that clone partners from
  // another project"). Both go through the one copy route the
  // Participants page uses - it carries the source project's people, rates
  // and settings, never emails; a partner already there is left as it is.
  const [toProject, setToProject] = useState(null);   // { mode, target, source, off } for one partner
  const [clone, setClone] = useState(null);           // { from, to, picked: [] }
  // COORDINATION ACCESS for the picked partner on one of its projects (owner,
  // 2026-10-15: "on the partner page, i don't see how to enable the
  // coordination tabs for them"). { rowId } of the project row being shown.
  const [coordFor, setCoordFor] = useState(null);

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    getPartnerDirectory()
      .then((r) => {
        const orgs = r.orgs || [];
        setState({ loading: false, orgs, projects: r.projects || [], error: "" });
        setPicked((p) => (orgs.some((o) => o.companyId === p) ? p : (orgs[0] ? orgs[0].companyId : "")));
      })
      .catch((e) => setState({ loading: false, orgs: [], projects: [], error: e.message || "Could not read your partners" }));
  }, []);
  useEffect(() => { if (allowed) load(); }, [allowed, load]);

  const org = useMemo(() => state.orgs.find((o) => o.companyId === picked) || null, [state.orgs, picked]);
  const people = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (org ? org.people : []).filter((p) => !q || `${p.name} ${p.email} ${p.title}`.toLowerCase().includes(q));
  }, [org, filter]);

  if (userInfo && !allowed) return <Redirect to="/coordination" />;

  const everywhere = org ? `all ${org.projects.length} project${org.projects.length === 1 ? "" : "s"} ${org.name} is on` : "";
  // Where ONE person is (projects are specific since 2026-10-15).
  const theirProjects = (p) => ((p && p.projects) || []).length;

  const titleOf = (id) => ((state.projects.find((p) => p.projectId === id) || {}).title || id);
  const say = (r, to) => {
    const copied = (r && r.copied) || [];
    const already = ((r && r.skipped) || []).filter((x) => x.why === "already on this project");
    const bits = [];
    if (copied.length) bits.push(`${copied.map((c) => c.companyName).join(", ")} added to ${titleOf(to)} with ${copied.reduce((t, c) => t + (c.people || 0), 0)} people`);
    if (already.length) bits.push(`${already.length} already there - left as it is (add more of its people from that project's Partners page)`);
    message.success(`${bits.join("; ") || "Nothing to add"}. Nobody was emailed - invite from the project's Participants page.`);
    const left = copied.filter((c) => (c.notCarried || []).length);
    if (left.length) message.warning(`Not carried over - only a company admin can grant these: ${left.map((c) => `${c.companyName} (${c.notCarried.join(", ")})`).join("; ")}.`, 10);
  };
  const addToProject = async () => {
    setBusy(true);
    try {
      const r = await copyParticipants(toProject.target, toProject.source, [org.companyId]);
      say(r, toProject.target);
      setToProject(null);
      load();
    } catch (e) { message.error(e.message || "Could not add the partner to that project"); } finally { setBusy(false); }
  };
  // TAKE THE PARTNER OFF ONE PROJECT (owner, 2026-10-05: "we need a also to
  // remove from a proejct, in teh same popup"). The route is a SOFT remove -
  // it sets the row's status to 'removed' and keeps it - so the partner's
  // history on that project survives and adding them back revives the same
  // row rather than inserting beside it (see the copy route; the index on
  // projectId+companyId is unique and a removed row is still in it).
  const removeFromProject = async () => {
    const proj = (org.projects || []).find((p) => p.projectId === toProject.off);
    if (!proj) return;
    setBusy(true);
    try {
      await removeParticipant(proj.projectId, proj.rowId);
      message.success(`${org.name} removed from ${proj.title}. Their people keep their accounts and stay on ${org.projects.length - 1 || "no"} other project${org.projects.length - 1 === 1 ? "" : "s"} - add them back from this same popup.`);
      setToProject(null);
      load();
    } catch (e) { message.error(e.message || "Could not remove the partner from that project"); } finally { setBusy(false); }
  };
  const runClone = async () => {
    setBusy(true);
    try {
      const r = await copyParticipants(clone.to, clone.from, clone.picked);
      say(r, clone.to);
      setClone(null);
      load();
    } catch (e) { message.error(e.message || "Could not clone the partners"); } finally { setBusy(false); }
  };
  const openProjects = state.projects.filter((p) => !p.archived);
  const partnersOn = (projectId) => state.orgs.filter((o) => o.projects.some((p) => p.projectId === projectId));

  const saveNew = async (v) => {
    setBusy(true);
    try {
      const body = { name: v.name, email: v.email, title: v.title, kind: v.kind, projectIds: v.projectIds || [] };
      const r = await addPartnerPerson(org.companyId, body);
      message.success(`${v.name} added to ${r.projects} project${r.projects === 1 ? "" : "s"} - not invited, and muted for scheduled reports there`);
      setAdding(null);
      load();
    } catch (e) { message.error(e.message || "Could not add the person"); } finally { setBusy(false); }
  };
  const saveEdit = async () => {
    setBusy(true);
    try {
      const body = { email: editing.email, name: editing.name };
      // A title is what users[] requires, so send it whenever the person is
      // or is becoming one.
      if (editing.kindNow === "person") body.title = editing.title;
      if (editing.supervisor || editing.executive) body.executive = !!editing.executiveNow;
      // Only send `kind` when it actually changes. Sending it unchanged would
      // make every name edit a privilege write, and on a both-listed person
      // the server refuses it - correctly, but there is nothing to refuse if
      // we do not ask.
      if (!editing.both && editing.kindNow && editing.kindNow !== kindOf(editing)) body.kind = editing.kindNow;
      const r = await updatePartnerPerson(org.companyId, body);
      message.success(`Updated on ${r.projects} project${r.projects === 1 ? "" : "s"}`);
      setEditing(null);
      load();
    } catch (e) { message.error(e.message || "Could not update the person"); } finally { setBusy(false); }
  };
  // GRANT A LOGIN. Creates the account only - nobody is emailed, which is
  // why this can live on a page whose whole promise is that it sends nothing.
  const grant = async (p) => {
    setBusy(true);
    try {
      const r = await grantPartnerAccount(org.companyId, p.email);
      message.success(r.created
        ? `${p.name} now has an account - nobody was emailed. Invite them from a project's Participants page when they should get in.`
        : `${p.name} already had an account; it is now linked on ${r.projects} project${r.projects === 1 ? "" : "s"}.`);
      load();
    } catch (e) { message.error(e.message || "Could not grant the account"); } finally { setBusy(false); }
  };
  // ONE PERSON, WHICH PROJECTS (owner, 2026-10-05). The partner is on N
  // projects; a person may be on a subset. Six people on prod already are.
  const savePersonProjects = async () => {
    setBusy(true);
    try {
      const r = await setPartnerPersonProjects(org.companyId, projectsFor.person.email, projectsFor.picked);
      const bits = [];
      if (r.added) bits.push(`added to ${r.added}`);
      if (r.removed) bits.push(`taken off ${r.removed}`);
      message.success(`${projectsFor.person.name}: ${bits.join(", ") || "no change"} - now on ${projectsFor.picked.length} of ${org.projects.length} of ${org.name}'s projects. Nobody was emailed.`);
      setProjectsFor(null);
      load();
    } catch (e) { message.error(e.message || "Could not change their projects"); } finally { setBusy(false); }
  };
  const remove = async (p) => {
    try {
      const r = await removePartnerPerson(org.companyId, p.email);
      message.success(`${p.name} removed from ${r.projects} project${r.projects === 1 ? "" : "s"}`);
      load();
    } catch (e) { message.error(e.message || "Could not remove the person"); }
  };

  const columns = [
    {
      title: "Name", key: "name",
      render: (_, p) => (
        <span>
          <b>{p.name}</b> {kindTag(p)}
          {p.conflicts ? (
            <Tooltip title={`Differs between projects - names: ${p.conflicts.names.join(" / ")}${p.conflicts.titles.length > 1 ? `; titles: ${p.conflicts.titles.join(" / ")}` : ""}. Edit to set one everywhere.`}>
              <Tag color="orange" style={{ marginLeft: 6 }}>differs by project</Tag>
            </Tooltip>
          ) : null}
        </span>
      ),
    },
    { title: "Title", key: "title", width: 170, render: (_, p) => p.title || <span style={{ color: T.muted }}>–</span> },
    { title: "Email", dataIndex: "email", key: "email", width: 230 },
    {
      title: "Projects", key: "projects", width: 120,
      // CLICKABLE since 2026-10-05: the count was visible and unchangeable.
      render: (_, p) => (
        <Tooltip title={`${org.projects.filter((x) => p.projects.includes(x.projectId)).map((x) => x.title).join(", ") || "none"} - click to change`}>
          <Button size="small" type="link" style={{ padding: 0 }}
            onClick={() => setProjectsFor({ person: p, picked: org.projects.filter((x) => p.projects.includes(x.projectId)).map((x) => x.projectId) })}>
            {p.projects.length} of {org.projects.length}
          </Button>
        </Tooltip>
      ),
    },
    {
      title: "Account", key: "account", width: 190,
      // Three states, not two: an account, an invite already sent, or
      // nothing. A button that silently re-invites somebody is a worse bug
      // than a missing button, so the pending case says so and offers
      // nothing. Supervisors and executives do not need accounts at all.
      // WHICH KIND OF ACCESS, on top of the three states above (owner,
      // 2026-10-05: "the account column should say what type of account, is
      // it roaster account, member of a standard account, magic link
      // access, or standard account"). The chip is shared with the Access
      // dialog so the column and the popup cannot name the same thing
      // differently; the Grant account button is untouched.
      render: (_, p) => {
        if (!p.person) return <AccountCell person={p} />;
        if (p.account) return <AccountCell person={p} />;
        return (
          <span style={{ whiteSpace: "nowrap" }}>
            <AccountCell person={p} />
            <Popconfirm
              title={`Create a login for ${p.name}?`}
              description={`${p.email} - the account is created now and NOBODY is emailed. Invite them from a project's Participants page when they should get in.`}
              okText="Create account" onConfirm={() => grant(p)}
            >
              <Button size="small" type="link" disabled={busy}>Grant account</Button>
            </Popconfirm>
          </span>
        );
      },
    },
    {
      title: "", key: "a", width: 210,
      render: (_, p) => (
        <span style={{ whiteSpace: "nowrap" }}>
          {/* EVERYTHING THIS PERSON CAN REACH, AND WHY - account, roster,
              permissions, links - with the checkboxes that change the
              partner-side grants. */}
          <Button size="small" type="link" onClick={() => setAccess({ email: p.email, name: p.name })}>Access</Button>
          <Button size="small" type="link" onClick={() => setEditing({ ...p, executiveNow: p.executive, kindNow: kindOf(p), both: !!(p.person && (p.supervisor || p.executive)) })}>Edit</Button>
          <Popconfirm title={`Remove ${p.name} from ${everywhere}?`} okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => remove(p)}>
            <Button size="small" type="link" danger>Remove</Button>
          </Popconfirm>
        </span>
      ),
    },
  ];

  return (
    <div style={{ background: T.bg, minHeight: "calc(100 * var(--vh))", padding: "24px 24px 48px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {embedded ? null : <CoordinationBreadcrumbs current="Partners" />}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 16 }}>
          <div>
            {embedded ? null : <h2 style={{ margin: 0 }}><TeamOutlined /> Partners</h2>}
            <div style={{ color: T.muted, marginTop: 4, maxWidth: 760 }}>
              Your partner organisations and all their people across your projects, with the projects each person is on.
              A name or title changed here changes wherever they are; which projects they are on is chosen per person.
              Nobody is emailed: people added here are muted for each project&apos;s scheduled reports until switched on in
              Reports &amp; notifications, and you invite them from a project&apos;s Participants page. Hourly rates stay
              per project.
            </div>
          </div>
          <span style={{ display: "flex", gap: 8 }}>
            <Button icon={<CopyOutlined />} disabled={!state.orgs.length} onClick={() => setClone({ from: undefined, to: undefined, picked: [] })}>
              Clone partners from a project
            </Button>
            <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
          </span>
        </div>
        {state.error ? <Alert type="error" showIcon message={state.error} style={{ marginBottom: 12 }} /> : null}
        {state.loading && !state.orgs.length ? <Skeleton active /> : !state.orgs.length ? (
          <Card><Empty description="No partners on your projects yet - add one from a project's Participants page" /></Card>
        ) : (
          <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
            <Card size="small" title={`Partners (${state.orgs.length})`} style={{ width: 280, flex: "0 0 280px" }} bodyStyle={{ padding: 0, maxHeight: 640, overflow: "auto" }}>
              {state.orgs.map((o) => (
                <div
                  key={o.companyId}
                  role="button"
                  tabIndex={0}
                  onClick={() => setPicked(o.companyId)}
                  onKeyDown={(e) => { if (e.key === "Enter") setPicked(o.companyId); }}
                  style={{
                    padding: "8px 12px", cursor: "pointer", borderBottom: `1px solid ${T.line || "#eee"}`,
                    background: o.companyId === picked ? "#EEF4FF" : "transparent",
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{o.name || "(no name)"}</div>
                  {orgTrades(o, disciplines).length ? (
                    <div style={{ fontSize: 11, color: T.muted }}>{orgTrades(o, disciplines).join(", ")}</div>
                  ) : null}
                  <div style={{ fontSize: 12, color: T.muted }}>
                    {o.people.length} {o.people.length === 1 ? "person" : "people"} · {o.projects.length} project{o.projects.length === 1 ? "" : "s"}
                  </div>
                </div>
              ))}
            </Card>
            {org ? (
              <Card
                size="small"
                style={{ flex: "1 1 600px", minWidth: 0 }}
                title={<PartnerWithTrades name={org.name} trades={orgTrades(org, disciplines)} />}
                extra={(
                  <span style={{ display: "flex", gap: 6 }}>
                    <Button size="small" disabled={!org.projects.length}
                            onClick={() => setCoordFor({ rowId: org.projects[0] && org.projects[0].rowId })}>Coordination access</Button>
                    <Button size="small" onClick={() => setToProject({ mode: "add", target: undefined, source: org.projects[0] && org.projects[0].projectId, off: undefined })}>Add or remove project</Button>
                    <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setAdding({ name: "", email: "", title: "", kind: "person", projectIds: org.projects.length === 1 ? [org.projects[0].projectId] : [] })}>Add person</Button>
                  </span>
                )}
              >
                <div style={{ marginBottom: 10, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                  <span style={{ color: T.muted, fontSize: 12 }}>On:</span>
                  {org.projects.map((p) => (
                    <Link key={p.projectId} to={`/partners?projectId=${p.projectId}`}><Tag style={{ cursor: "pointer" }}>{p.title || p.projectId}</Tag></Link>
                  ))}
                </div>
                {org.nameOnly ? (
                  <div style={{ color: T.muted, fontSize: 12, marginBottom: 6 }}>
                    {org.nameOnly} {org.nameOnly === 1 ? "person is" : "people are"} listed without an email - they are managed on
                    each project&apos;s Partners page until somebody adds their address.
                  </div>
                ) : null}
                <Input.Search allowClear placeholder="Find a person" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ maxWidth: 320, marginBottom: 10 }} />
                <Table size="small" rowKey="email" dataSource={people} columns={columns} pagination={{ pageSize: 25, hideOnSinglePage: true }}
                  locale={{ emptyText: "Nobody listed for this partner yet" }} />
              </Card>
            ) : null}
          </div>
        )}

        <Modal open={!!toProject && !!org} title={org ? `${org.name} - projects` : "Projects"}
          okText={toProject && toProject.mode === "remove" ? "Remove from project" : "Add"}
          okButtonProps={toProject && toProject.mode === "remove"
            ? { danger: true, disabled: !toProject.off }
            : { disabled: !toProject || !toProject.target || !toProject.source }}
          confirmLoading={busy} onCancel={() => setToProject(null)} destroyOnClose
          onOk={toProject && toProject.mode === "remove" ? removeFromProject : addToProject}>
          {toProject && org ? (
            <div style={{ display: "grid", gap: 10 }}>
              <Segmented block value={toProject.mode}
                onChange={(v) => setToProject({ ...toProject, mode: v })}
                options={[
                  { value: "add", label: "Add to a project" },
                  { value: "remove", label: "Remove from a project", disabled: !(org.projects || []).length },
                ]} />
              {toProject.mode === "remove" ? (
                <>
                  <div>
                    <div style={{ fontSize: 13, marginBottom: 4 }}>Project to take them off</div>
                    <Select filterOption={matchOption} showSearch optionFilterProp="label" style={{ width: "100%" }} placeholder="Pick a project"
                      value={toProject.off} onChange={(v) => setToProject({ ...toProject, off: v })}
                      options={(org.projects || []).map((p) => ({ value: p.projectId, label: p.title }))} />
                  </div>
                  {/* WHAT IT COSTS, SAID BEFORE THE CLICK. The partner's people
                      lose access to that project; they keep their accounts and
                      whatever they are on elsewhere. Anything they were made
                      responsible for on that project stays where it is - this
                      removes access, it does not reassign work. */}
                  <Alert type="warning" showIcon
                    message={org.projects.length === 1 ? "This is their only project" : "Their people lose access to that project"}
                    description={org.projects.length === 1
                      ? `${org.name} is on one project. Taking them off it leaves them on none, and they will drop out of this list - their people keep their accounts and you can add them back from here.`
                      : "They keep their accounts and stay on their other projects. Anything they were made responsible for there stays where it is - this removes access, it does not reassign work. Nobody is emailed."} />
                </>
              ) : (
              <>
              <div>
                <div style={{ fontSize: 13, marginBottom: 4 }}>Project</div>
                <Select filterOption={matchOption} showSearch optionFilterProp="label" style={{ width: "100%" }} placeholder="Pick a project"
                  value={toProject.target} onChange={(v) => setToProject({ ...toProject, target: v })}
                  options={openProjects.filter((p) => !org.projects.some((x) => x.projectId === p.projectId)).map((p) => ({ value: p.projectId, label: p.title }))}
                  notFoundContent={`${org.name} is already on every open project`} />
              </div>
              <div>
                <div style={{ fontSize: 13, marginBottom: 4 }}>Take its rates and settings from</div>
                <Select style={{ width: "100%" }} value={toProject.source} onChange={(v) => setToProject({ ...toProject, source: v })}
                  options={org.projects.map((p) => ({ value: p.projectId, label: p.title }))} />
              </div>
              <div style={{ color: T.muted, fontSize: 12 }}>
                Its people on {titleOf(toProject.source)} come with it - nobody else. Its people on other projects can be
                added there with &quot;Add from {org.name}&apos;s people&quot; on the project&apos;s Partners page. Nobody is
                emailed; invite them from the project&apos;s Participants page.
              </div>
              </>
              )}
            </div>
          ) : null}
        </Modal>

        <Modal open={!!clone} title="Clone partners from a project" okText="Clone" width={620}
          confirmLoading={busy} onCancel={() => setClone(null)} onOk={runClone} destroyOnClose
          okButtonProps={{ disabled: !clone || !clone.from || !clone.to || !clone.picked.length }}>
          {clone ? (
            <div style={{ display: "grid", gap: 10 }}>
              <div>
                <div style={{ fontSize: 13, marginBottom: 4 }}>From project</div>
                <Select filterOption={matchOption} showSearch optionFilterProp="label" style={{ width: "100%" }} placeholder="The project whose partners to clone"
                  value={clone.from}
                  onChange={(v) => setClone({ ...clone, from: v, to: clone.to === v ? undefined : clone.to, picked: partnersOn(v).map((o) => o.companyId) })}
                  options={state.projects.filter((p) => partnersOn(p.projectId).length).map((p) => ({ value: p.projectId, label: `${p.title} - ${partnersOn(p.projectId).length} partners` }))} />
              </div>
              <div>
                <div style={{ fontSize: 13, marginBottom: 4 }}>To project</div>
                <Select filterOption={matchOption} showSearch optionFilterProp="label" style={{ width: "100%" }} placeholder="Where they should work"
                  value={clone.to} onChange={(v) => setClone({ ...clone, to: v })}
                  options={openProjects.filter((p) => p.projectId !== clone.from).map((p) => ({ value: p.projectId, label: p.title }))} />
              </div>
              {clone.from ? (
                <div>
                  <div style={{ fontSize: 13, marginBottom: 4 }}>Partners</div>
                  {partnersOn(clone.from).map((o) => {
                    const there = !!clone.to && o.projects.some((p) => p.projectId === clone.to);
                    const on = clone.picked.includes(o.companyId);
                    return (
                      <label key={o.companyId} style={{ display: "flex", gap: 8, alignItems: "center", padding: "3px 0" }}>
                        <input type="checkbox" checked={on}
                          onChange={() => setClone({ ...clone, picked: on ? clone.picked.filter((x) => x !== o.companyId) : [...clone.picked, o.companyId] })} />
                        <span><b>{o.name}</b>{orgTrades(o, disciplines).length ? <span style={{ color: T.muted, fontSize: 11 }}> ({orgTrades(o, disciplines).join(", ")})</span> : null} <span style={{ color: T.muted }}>- {o.people.length} {o.people.length === 1 ? "person" : "people"}{there ? " - already there, left as it is" : ""}</span></span>
                      </label>
                    );
                  })}
                </div>
              ) : null}
              <div style={{ color: T.muted, fontSize: 12 }}>
                People, rates and settings come across as on the from-project (plan permissions only when a company admin
                clones). Nobody is emailed - invite from the project's Participants page.
              </div>
            </div>
          ) : null}
        </Modal>

        <Modal open={!!projectsFor && !!org} destroyOnClose
          title={projectsFor ? `${projectsFor.person.name} - which projects` : "Projects"}
          okText="Save" confirmLoading={busy} onCancel={() => setProjectsFor(null)} onOk={savePersonProjects}
          okButtonProps={{ disabled: !projectsFor || !projectsFor.picked.length }}>
          {projectsFor && org ? (
            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ color: T.muted, fontSize: 12 }}>
                {org.name} is on {org.projects.length} project{org.projects.length === 1 ? "" : "s"}. Tick the ones {projectsFor.person.name} should be on.
              </div>
              {org.projects.map((pr) => {
                const on = projectsFor.picked.includes(pr.projectId);
                return (
                  <label key={pr.projectId} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <input type="checkbox" checked={on}
                      onChange={() => setProjectsFor({
                        ...projectsFor,
                        picked: on ? projectsFor.picked.filter((x) => x !== pr.projectId) : [...projectsFor.picked, pr.projectId],
                      })} />
                    <span>{pr.title}</span>
                  </label>
                );
              })}
              {/* Why an empty tick-list is refused rather than treated as a
                  removal: taking somebody off every project is "they left the
                  partner", which is what Remove is for, and it clears their
                  listing instead of leaving marks on every row. */}
              {!projectsFor.picked.length ? (
                <Alert type="info" showIcon message="Pick at least one project"
                  description={`To take ${projectsFor.person.name} off ${org.name} entirely, use Remove on their row.`} />
              ) : null}
              <div style={{ color: T.muted, fontSize: 12 }}>
                Each project is its own choice - ticking or unticking one changes only that project. Nobody is emailed.
              </div>
            </div>
          ) : null}
        </Modal>

        {/* THE ONE ADD-PERSON DIALOG (components/Partners/PartnerPersonModal),
            the same one the project roster opens - here with the partner's
            projects to tick. */}
        <PartnerPersonModal
          open={!!adding}
          title={org ? `Add a person to ${org.name}` : "Add a person"}
          okText="Add"
          busy={busy}
          initial={adding}
          projects={org ? org.projects.map((pr) => ({ projectId: pr.projectId, title: pr.title })) : []}
          onCancel={() => setAdding(null)}
          onSave={saveNew}
          note={<>Added to the projects ticked only - not invited, and muted for those projects&apos; scheduled reports until
            switched on in Reports &amp; notifications. Invite from a project&apos;s Participants page when they should
            get access.</>}
        />

        <Modal open={!!editing} title={editing ? `Edit ${editing.name}` : "Edit"} okText="Save everywhere"
          confirmLoading={busy} onCancel={() => setEditing(null)} onOk={saveEdit} destroyOnClose
          okButtonProps={{ disabled: !editing || !editing.name.trim() || (editing.kindNow === "person" && !String(editing.title || "").trim()) }}>
          {editing ? (
            <div style={{ display: "grid", gap: 8 }}>
              <Input addonBefore="Name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              {editing.both ? (
                <Alert type="info" showIcon message="Listed as both a user and a supervisor"
                  description="That is allowed on purpose, so this page will not collapse them into one. Change them on the project where you want it changed, or remove one of the two listings first." />
              ) : (
                <Select value={editing.kindNow} onChange={(v) => setEditing({ ...editing, kindNow: v })} options={KINDS} />
              )}
              {editing.kindNow === "person" ? (
                <Input addonBefore="Title" value={editing.title || ""} placeholder="e.g. Foreman, PM" onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
              ) : null}
              {!editing.both && editing.kindNow !== kindOf(editing) ? (
                <Alert type="warning" showIcon
                  message={editing.kindNow === "person" ? "This removes their supervisor access" : editing.kindNow === "supervisor" ? "Supervisor is a permission, not a label" : "Executive receives the weekly summary and nothing else"}
                  description={editing.kindNow === "person"
                    ? "They stop receiving the partner digest and lose the supervisor permission on issues."
                    : editing.kindNow === "supervisor"
                      ? "A supervisor can view, raise and update issues on this partner's projects, and receives the partner digest."
                      : "An executive is a reader and recipient only - no operational role, no privileges, and no account is needed."} />
              ) : null}
              {(editing.supervisor || editing.executive) && editing.kindNow === kindOf(editing) && editing.kindNow !== "person" ? (
                <div><Switch checked={!!editing.executiveNow} onChange={(v) => setEditing({ ...editing, executiveNow: v })} /> <span style={{ marginLeft: 6 }}>Executive - weekly summary only, no operational role</span></div>
              ) : null}
              <div style={{ color: T.muted, fontSize: 12 }}>{editing.email} - saved on the {theirProjects(editing)} project{theirProjects(editing) === 1 ? "" : "s"} they are on.</div>
            </div>
          ) : null}
        </Modal>

        {/* Which coordination tabs this partner gets, ONE PROJECT AT A TIME -
            the switches live on the partner's row of each project, because
            "yes on one job, no on another" is the question the owner asks. */}
        <Modal open={!!coordFor && !!org} title={org ? `${org.name} - coordination access` : "Coordination access"}
          onCancel={() => setCoordFor(null)} footer={[<Button key="c" onClick={() => setCoordFor(null)}>Close</Button>]} width={700} destroyOnClose>
          {coordFor && org ? (
            <div>
              <div style={{ marginBottom: 10, display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ color: T.muted }}>Project</span>
                <Select style={{ minWidth: 320 }} value={coordFor.rowId} aria-label="Project"
                        onChange={(rowId) => setCoordFor({ rowId })}
                        options={org.projects.map((p) => ({ value: p.rowId, label: p.title || p.projectId }))} />
              </div>
              {coordFor.rowId ? <CoordinationAccessGrid key={coordFor.rowId} participantId={coordFor.rowId} /> : null}
            </div>
          ) : null}
        </Modal>

        {/* Mounted only while somebody is being looked at, and keyed on the
            address: opening a second person must re-read rather than show
            the first one's grants under the second one's name. */}
        {access ? (
          <PartnerAccessDialog
            key={access.email}
            email={access.email}
            name={access.name}
            onClose={() => setAccess(null)}
          />
        ) : null}
      </div>
    </div>
  );
}
